import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';
import StackedIcon from '@components/Icon/StackedIcon';

import { formatDate } from '@utils/format';

import { useListAlertNotificationsLazyQuery } from 'generated/alert.generated';
import { AlertNotificationStatus } from 'generated/types';

interface AlertNotificationModalContentProps {
  alert_id: string;
}

const messages = defineMessages({
  noNotifications: {
    id: 'alertNotificationModalContent.noNotifications',
    defaultMessage: 'There were no notifications sent for this alert.',
  },
  alreadySent: {
    id: 'alertNotificationModalContent.alreadySent',
    defaultMessage:
      'A notification had already been sent for the same metric within a 24 hour period.',
  },
  sentAt: {
    id: 'alertNotificationModalContent.sentAt',
    defaultMessage: 'Sent: ',
  },
});

export default function AlertNotificationModalContent({
  alert_id,
}: AlertNotificationModalContentProps) {
  const { formatMessage } = useIntl();
  const { failed, not_delivered, success } = AlertNotificationStatus;

  const [getNotifications, { data, loading }] =
    useListAlertNotificationsLazyQuery();

  useEffect(() => {
    const fetchNotifications = async () => {
      await getNotifications({
        variables: {
          input: {
            alert_id,
          },
        },
      });
    };

    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert_id]);

  /*
  // TODO: Use 'messages.alreadySent' text
  if (!data) {
    return (
      <Typography color="text.secondary" variant="subtitle2">
        {formatMessage(messages.noNotifications)}
      </Typography>
    );
  }
    */

  const determineColor = (status?: AlertNotificationStatus) => {
    switch (status) {
      case failed:
        return 'error.main';
      case not_delivered:
        return 'text.secondary';
      case success:
        return 'success.main';
      default:
        return 'text.primary';
    }
  };

  return (
    <ContentLoader lineCount={4} loading={loading}>
      {data?.listAlertNotifications.map(
        ({
          email_notification,
          sms_notification,
          name,
          recipient_id,
          type,
        }) => {
          const emailColor = determineColor(email_notification?.status);
          const smsColor = determineColor(sms_notification?.status);

          const emailIcon = (
            <StackedIcon
              icons={
                email_notification?.error_message
                  ? [
                      {
                        name: 'envelope',
                        color: emailColor,
                        size: 'lg',
                        type: 'fas',
                      },
                      {
                        name: 'circle-xmark',
                        color: emailColor,
                        size: 'xs',
                        type: 'fas',
                        transform: {
                          x: 15,
                          y: 3,
                        },
                        style: {
                          stroke: 'white',
                          strokeWidth: 40,
                        },
                      },
                    ]
                  : [
                      {
                        name: 'envelope-circle-check',
                        color: emailColor,
                        size: 'lg',
                        type: 'fas',
                      },
                    ]
              }
            />
          );
          const smsIcon = (
            <Icon
              color={smsColor}
              name={
                sms_notification?.error_message
                  ? 'message-xmark'
                  : 'message-sms'
              }
              size="lg"
              type="fas"
            />
          );

          return (
            <Box key={`alert-recipient-${type}-${recipient_id}`} sx={{ mb: 1 }}>
              <Typography color="primary" variant="subtitle1">
                {name}
              </Typography>
              <List dense>
                {email_notification && (
                  <ListItem disablePadding sx={{ ml: 1, minHeight: 'auto' }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      {emailIcon}
                    </ListItemIcon>
                    <ListItemText
                      primary={email_notification.destination}
                      secondary={
                        email_notification.error_message ??
                        formatMessage(messages.sentAt, {
                          date: formatDate({
                            date: email_notification.sent_at,
                            formatter: 'datetime',
                          }),
                        })
                      }
                    />
                  </ListItem>
                )}
                {sms_notification && (
                  <ListItem disablePadding sx={{ ml: 1, minHeight: 'auto' }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>{smsIcon}</ListItemIcon>
                    <ListItemText
                      primary={sms_notification.destination}
                      secondary={
                        sms_notification.error_message ??
                        formatMessage(messages.sentAt, {
                          date: formatDate({
                            date: sms_notification.sent_at,
                            formatter: 'datetime',
                          }),
                        })
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          );
        }
      )}
    </ContentLoader>
  );
}
